import type { FC } from 'react'
import type { ERC20Names } from '@g4g/wallet-modal/types'
import CartButton from '../../../../shopping-cart/buttons/CartButton'
import CheckoutPaymentTxOverlay from './CheckoutPaymentTxOverlay'
import useGasFeeCheck from '@g4g/wallet-modal/hooks/wallet/useGasFeeCheck'
import useTransaction from '@g4g/wallet-modal/hooks/wallet/useTransaction'
import { useUpdateEffect } from 'react-use'
import CheckoutPaymentAttention from './CheckoutPaymentAttention'
import { useBurnOpsDispatch } from '../../../BurnProvider'
import gtm_purchase from '@/shop/src/utils/gtm/gtm_purchase'
import gtm_refund from '@/shop/src/utils/gtm/gtm_refund'

const CheckoutPaymentSubmitReady: FC<{
  paymentOption: ERC20Names
  amount: string
  hodlKeys: string[]
  payload: string
}> = ({ paymentOption, amount, hodlKeys, payload }) => {
  const dispatch = useBurnOpsDispatch()
  const hasEnough = useGasFeeCheck(
    paymentOption,
    'burn',
    amount,
    ...(paymentOption === 'Purpose' ? [hodlKeys, payload] : [payload])
  )
  const {
    loading: txLoading,
    submit,
    error,
    txStatus,
    data,
    reset,
  } = useTransaction(
    async (provider) => provider.burnERC20(paymentOption, amount, payload, hodlKeys),
    [paymentOption, amount, payload, hodlKeys]
  )

  useUpdateEffect(() => {
    if (error || !data.submittedTx || !data.chainId || !payload) {
      // In case of a transaction submit error
      // we stay on the /checkout page & display
      // the error, atm the only option is to try again,
      // it's also being dispatched to the global Burn
      // Transaction state
      dispatch({ type: 'set-error', error })
      // Send a refund event if we have the purchase id (hash)
      if (data.submittedTx?.hash) {
        gtm_refund({ txHash: data.submittedTx.hash, tokenName: paymentOption })
      }
      return
    }
    gtm_purchase({
      txHash: data.submittedTx.hash,
      tokenName: paymentOption,
      amount,
    })
    // Set the global Burn Transaction state
    // to the successfully submitted transaction
    dispatch({
      type: 'set-submitted-transaction',
      submittedTx: data.submittedTx,
      chainId: data.chainId,
      payload,
      currency: paymentOption,
    })
  }, [txStatus, data, dispatch, error, amount])

  return (
    <>
      {(txLoading || error) && (
        <CheckoutPaymentTxOverlay
          paymentOption={paymentOption}
          loading={txLoading}
          error={error}
          closeOverlay={reset}
        />
      )}
      <div className="flex flex-col justify-center w-full h-full space-y-2">
        <CartButton
          data-cy="shop-checkout-submit-btn"
          className="h-11"
          onClick={() => hasEnough.value && submit()}
          disabled={!hasEnough.value}
          loading={hasEnough.loading || txLoading}
        >
          Pay Now
        </CartButton>
        <CheckoutPaymentAttention
          hasEnoughForGas={hasEnough.value}
          loading={hasEnough.loading}
          error={hasEnough.error}
          paymentOption={paymentOption}
        />
      </div>
    </>
  )
}

export default CheckoutPaymentSubmitReady
